import {FeatureModel} from "./integration/representation/viewmodel";
import {Locational} from "./integration/representation/feature";
import {LaneFilter} from "./presentation/routes/contract-features-3/components/cellComponents/LaneModal";
import {Direction, FeltKode, Lane, LaneEdit} from "./presentation/routes/contract-features-3/domain";
import {noLocationals} from "./locationalUtilities";
import {MeteringDirectionMissingError} from "./meteringDirectionUtilities";

function arraysContainSameValues(array1: string[], array2: string[]): boolean {
    return array1.sort().join(',') === array2.sort().join(',')
}

export function getLaneFilters(): LaneFilter[] {
    return [
        {name: "Ordinært kjørefelt", term: "kjørefelt"},
        {name: "Forbikjøring", term: "forbikjøring"},
        {name: "Reversibelt", term: "revers"},
        {name: "Kollektivfelt", term: "kollektiv"},
        {name: "Sambruksfelt", term: "sambruk"},
        {name: "Sykkelfelt", term: "sykkel"},
        {name: "Bomstasjon", term: "boms"},
        {name: "Oppstillingsfelt", term: "oppstilling"},
        {name: "Høyresvingsfelt", term: "høyresving"},
        {name: "Venstresvingsfelt", term: "venstresving"},
    ]
}

export function getLanesForFeatureRelativeToMetering(feature: FeatureModel): Lane[] {
    if (lanesEqualForAllLocationalsRelativeToMeteringDirection(feature)) {
        return getLanesRelativeToMeteringDirection(feature.locational.locationals[0]);
    } else {
        throw "Kan ikke oppgi felt for vegobjekt når ikke alle sideposisjoner er like.";
    }
}

export function lanesEqualForAllLocationalsRelativeToMeteringDirection(feature: FeatureModel): boolean {
    // Returns true if all locationals for a feature has the same lanes relative to metering direction.
    // For instance, a road object that has two locationals. The first locational has the lanes "R1" and "R2". The
    // second locational also has "R1" and "R2". Then this method returns true.
    // It also returns true if there are no locationals or if none of the locationals does not have lanes.
    if (!feature.locational) {
        return true;
    }
    const lanesPerLocational: string[][] = feature.locational.locationals
        .map(locational => getLanesRelativeToMeteringDirection(locational));
    return lanesPerLocational.every(laneArray => arraysContainSameValues(laneArray, lanesPerLocational[0]));
}

export function getAllLanesAsTextRelativeToMeteringDirection(feature: FeatureModel): string {
    if (noLocationals(feature)) {
        return "";
    }
    const flattened: Lane[] = [];
    feature.locational.locationals
        .map(locational => getLanesRelativeToMeteringDirection(locational))
        .forEach(lanes => flattened.push(...lanes));

    const unique = [...new Set(flattened)];

    const sorted = unique.sort();

    return sorted.join(", ")
}

export function getLanesRelativeToMeteringDirection(locational: Locational): Lane[] {
    if (locational === null) {
        return null;
    }
    const lanes = locational.lanes;
    const meteringDirection = locational.meteringDirection;
    const lanesRelativeToMeteringDirection: Lane[] = [];
    for (let lane of lanes) {
        const laneRelativeToMeteringDirection = reverseLaneIfMeteringDirectionAgainst(lane, meteringDirection);
        lanesRelativeToMeteringDirection.push(laneRelativeToMeteringDirection);
    }
    return lanesRelativeToMeteringDirection;
}

export function reverseLaneIfMeteringDirectionAgainst(lane: Lane, meteringDirection: Direction): Lane {
    if (meteringDirection === "MOT") {
        return reverseLane(lane);
    }
    if (meteringDirection === "MED") {
        return lane;
    }
    if (!isMeteringDirectionRelevant(lane)) {
        return lane;
    }
    // The lane is dependent on the metering direction, but the metering direction is unknown
    // We can't know the lane
    throw new MeteringDirectionMissingError("Kan ikke oppgi felt når metreringsretning mangler");
}

function reverseLane(lane: Lane): Lane {
    if (lane === '1R') {
        return '1R';
    }
    const indexOfFirstLetter = getIndexOfFirstLetter(lane);
    const laneNumber = lane.slice(0, indexOfFirstLetter);
    const laneType = lane.slice(indexOfFirstLetter);
    const laneNumberAsNumber = parseInt(laneNumber);
    const reversedLaneNumber = reverseLaneNumber(laneNumberAsNumber);
    return reversedLaneNumber + laneType as Lane;
}

function reverseLaneNumber(laneNumber: number): number {
    if (laneNumber === 0) {
        return 0
    }
    if (laneNumber % 2 === 0) {
        return laneNumber - 1;
    } else {
        return laneNumber + 1;
    }
}

function getIndexOfFirstLetter(lane: Lane) {
    const firstLetter = /[a-z]/i.exec(lane);
    if (firstLetter) {
        return firstLetter.index;
    } else {
        return lane.length;
    }
}

function isMeteringDirectionRelevant(lane: Lane): boolean {
    return lane !== '0' && lane !== '1R';
}

export function createLaneEdit(locational: Locational, newLanesRelativeToMeteringDirection: Lane[], featureId: string): LaneEdit {
    const meteringDirection = locational.meteringDirection;
    const newLanesRelativeToGeometryDirection = newLanesRelativeToMeteringDirection.map(lane => reverseLaneIfMeteringDirectionAgainst(lane, meteringDirection));
    const newLanesAsString = newLanesRelativeToGeometryDirection.join("#");
    const linkId = locational.linkId;
    return new LaneEdit(newLanesAsString, featureId, linkId);
}

export function getFeltKodeList(): Array<FeltKode> {
    return [
        {value: "0", name: "Alle kjørefelt"},
        {value: "1", name: "1 Ordinært kjørefelt"},
        {value: "2", name: "2 Ordinært kjørefelt"},
        {value: "3", name: "3 Ordinært kjørefelt"},
        {value: "4", name: "4 Ordinært kjørefelt"},
        {value: "5", name: "5 Ordinært kjørefelt"},
        {value: "6", name: "6 Ordinært kjørefelt"},
        {value: "7", name: "7 Ordinært kjørefelt"},
        {value: "8", name: "8 Ordinært kjørefelt"},
        {value: "9", name: "9 Ordinært kjørefelt"},
        {value: "10", name: "10 Ordinært kjørefelt"},
        {value: "11", name: "11 Ordinært kjørefelt"},
        {value: "12", name: "12 Ordinært kjørefelt"},
        {value: "1F", name: "1F Forbikjøringsfelt"},
        {value: "2F", name: "2F Forbikjøringsfelt"},
        {value: "1R", name: "1R Reversibelt kjørefelt"},
        {value: "1K", name: "1K Kollektivfelt"},
        {value: "2K", name: "2K Kollektivfelt"},
        {value: "3K", name: "3K Kollektivfelt"},
        {value: "4K", name: "4K Kollektivfelt"},
        {value: "5K", name: "5K Kollektivfelt"},
        {value: "6K", name: "6K Kollektivfelt"},
        {value: "7K", name: "7K Kollektivfelt"},
        {value: "8K", name: "8K Kollektivfelt"},
        {value: "9K", name: "9K Kollektivfelt"},
        {value: "10K", name: "10K Kollektivfelt"},
        {value: "11K", name: "11K Kollektivfelt"},
        {value: "12K", name: "12K Kollektivfelt"},
        {value: "1K+", name: "1K+ Sambruksfelt"},
        {value: "2K+", name: "2K+ Sambruksfelt"},
        {value: "3K+", name: "3K+ Sambruksfelt"},
        {value: "4K+", name: "4K+ Sambruksfelt"},
        {value: "5K+", name: "5K+ Sambruksfelt"},
        {value: "6K+", name: "6K+ Sambruksfelt"},
        {value: "7K+", name: "7K+ Sambruksfelt"},
        {value: "8K+", name: "8K+ Sambruksfelt"},
        {value: "9K+", name: "9K+ Sambruksfelt"},
        {value: "10K+", name: "10K+ Sambruksfelt"},
        {value: "11K+", name: "11K+ Sambruksfelt"},
        {value: "12K+", name: "12K+ Sambruksfelt"},
        {value: "1S", name: "1S Sykkelfelt"},
        {value: "2S", name: "2S Sykkelfelt"},
        {value: "3S", name: "3S Sykkelfelt"},
        {value: "4S", name: "4S Sykkelfelt"},
        {value: "5S", name: "5S Sykkelfelt"},
        {value: "6S", name: "6S Sykkelfelt"},
        {value: "7S", name: "7S Sykkelfelt"},
        {value: "8S", name: "8S Sykkelfelt"},
        {value: "9S", name: "9S Sykkelfelt"},
        {value: "10S", name: "10S Sykkelfelt"},
        {value: "11S", name: "11S Sykkelfelt"},
        {value: "12S", name: "12S Sykkelfelt"},
        {value: "13S", name: "13S Sykkelfelt"},
        {value: "14S", name: "14S Sykkelfelt"},
        {value: "3B", name: "3B Bomstasjon"},
        {value: "4B", name: "4B Bomstasjon"},
        {value: "5B", name: "5B Bomstasjon"},
        {value: "6B", name: "6B Bomstasjon"},
        {value: "7B", name: "7B Bomstasjon"},
        {value: "8B", name: "8B Bomstasjon"},
        {value: "9B", name: "9B Bomstasjon"},
        {value: "10B", name: "10B Bomstasjon"},
        {value: "11B", name: "11B Bomstasjon"},
        {value: "12B", name: "12B Bomstasjon"},
        {value: "13B", name: "13B Bomstasjon"},
        {value: "14B", name: "14B Bomstasjon"},
        {value: "3BK", name: "3BK Bomstasjon, kollektivfelt"},
        {value: "4BK", name: "4BK Bomstasjon, kollektivfelt"},
        {value: "5BK", name: "5BK Bomstasjon, kollektivfelt"},
        {value: "6BK", name: "6BK Bomstasjon, kollektivfelt"},
        {value: "7BK", name: "7BK Bomstasjon, kollektivfelt"},
        {value: "8BK", name: "8BK Bomstasjon, kollektivfelt"},
        {value: "9BK", name: "9BK Bomstasjon, kollektivfelt"},
        {value: "10BK", name: "10BK Bomstasjon, kollektivfelt"},
        {value: "11BK", name: "11BK Bomstasjon, kollektivfelt"},
        {value: "12BK", name: "12BK Bomstasjon, kollektivfelt"},
        {value: "13BK", name: "13BK Bomstasjon, kollektivfelt"},
        {value: "14BK", name: "14BK Bomstasjon, kollektivfelt"},
        {value: "1O", name: "1O Oppstillingsfelt"},
        {value: "2O", name: "2O Oppstillingsfelt"},
        {value: "3O", name: "3O Oppstillingsfelt"},
        {value: "4O", name: "4O Oppstillingsfelt"},
        {value: "5O", name: "5O Oppstillingsfelt"},
        {value: "6O", name: "6O Oppstillingsfelt"},
        {value: "7O", name: "7O Oppstillingsfelt"},
        {value: "8O", name: "8O Oppstillingsfelt"},
        {value: "9O", name: "9O Oppstillingsfelt"},
        {value: "10O", name: "10O Oppstillingsfelt"},
        {value: "11O", name: "11O Oppstillingsfelt"},
        {value: "12O", name: "12O Oppstillingsfelt"},
        {value: "13O", name: "13O Oppstillingsfelt"},
        {value: "14O", name: "14O Oppstillingsfelt"},
        {value: "1OK", name: "1OK Oppstilling, kollektivfelt"},
        {value: "2OK", name: "2OK Oppstilling, kollektivfelt"},
        {value: "3OK", name: "3OK Oppstilling, kollektivfelt"},
        {value: "4OK", name: "4OK Oppstilling, kollektivfelt"},
        {value: "5OK", name: "5OK Oppstilling, kollektivfelt"},
        {value: "6OK", name: "6OK Oppstilling, kollektivfelt"},
        {value: "7OK", name: "7OK Oppstilling, kollektivfelt"},
        {value: "8OK", name: "8OK Oppstilling, kollektivfelt"},
        {value: "9OK", name: "9OK Oppstilling, kollektivfelt"},
        {value: "10OK", name: "10OK Oppstilling, kollektivfelt"},
        {value: "11OK", name: "11OK Oppstilling, kollektivfelt"},
        {value: "12OK", name: "12OK Oppstilling, kollektivfelt"},
        {value: "13OK", name: "13OK Oppstilling, kollektivfelt"},
        {value: "14OK", name: "14OK Oppstilling, kollektivfelt"},
        {value: "1H1", name: "1H1 Høyresvingefelt 1"},
        {value: "2H1", name: "2H1 Høyresvingefelt 1"},
        {value: "3H1", name: "3H1 Høyresvingefelt 1"},
        {value: "4H1", name: "4H1 Høyresvingefelt 1"},
        {value: "5H1", name: "5H1 Høyresvingefelt 1"},
        {value: "6H1", name: "6H1 Høyresvingefelt 1"},
        {value: "7H1", name: "7H1 Høyresvingefelt 1"},
        {value: "8H1", name: "8H1 Høyresvingefelt 1"},
        {value: "9H1", name: "9H1 Høyresvingefelt 1"},
        {value: "10H1", name: "10H1 Høyresvingefelt 1"},
        {value: "11H1", name: "11H1 Høyresvingefelt 1"},
        {value: "12H1", name: "12H1 Høyresvingefelt 1"},
        {value: "1H2", name: "1H2 Høyresvingefelt 2"},
        {value: "2H2", name: "2H2 Høyresvingefelt 2"},
        {value: "3H2", name: "3H2 Høyresvingefelt 2"},
        {value: "4H2", name: "4H2 Høyresvingefelt 2"},
        {value: "5H2", name: "5H2 Høyresvingefelt 2"},
        {value: "6H2", name: "6H2 Høyresvingefelt 2"},
        {value: "7H2", name: "7H2 Høyresvingefelt 2"},
        {value: "8H2", name: "8H2 Høyresvingefelt 2"},
        {value: "9H2", name: "9H2 Høyresvingefelt 2"},
        {value: "10H2", name: "10H2 Høyresvingefelt 2"},
        {value: "11H2", name: "11H2 Høyresvingefelt 2"},
        {value: "12H2", name: "12H2 Høyresvingefelt 2"},
        {value: "1V1", name: "1V1 Venstresvingefelt 1"},
        {value: "1V2", name: "1V2 Venstresvingefelt 2"},
        {value: "2V1", name: "2V1 Venstresvingefelt 1"},
        {value: "2V2", name: "2V2 Venstresvingefelt 2"},
    ]
}
